import { getImpactRank, getRoadworkCoords } from '../../components/roadworks/roadworkMapUtils';
import type {
  RoadworkItem,
  RoadworksStatusResponse,
} from '../services/roadworksApiService';
import { EDINBURGH_COUNCIL_BOUNDARY } from './edinburghCouncilBoundary';

export type WorkSubcategory =
  | 'cycle_hanger'
  | 'shared_path'
  | 'signals_crossings'
  | 'cycling'
  | 'pedestrian'
  | 'road_improvement';

export type WorkTiming =
  | 'live'
  | 'next_30_days'
  | 'next_90_days'
  | 'later'
  | 'ended'
  | 'unscheduled';

export type WorkTimeFilter =
  | 'all'
  | 'now_and_soon'
  | 'live'
  | 'next_30_days'
  | 'next_90_days'
  | 'later';

export type DiscoveryState = 'new_refresh' | 'recently_seen' | 'established';
export type DataHealthLevel = 'healthy' | 'warning' | 'unknown';

export interface DataHealth {
  level: DataHealthLevel;
  label: string;
  summary: string;
  details: string[];
  updatedAt: Date | null;
  ageHours: number | null;
  coverageStart: Date | null;
  coverageEnd: Date | null;
  sourceCount: number | null;
  previousCount: number | null;
  countChangePercent: number | null;
  cacheHits: number | null;
  queryCalls: number | null;
  networkRequests: number | null;
  lookbackDays: number | null;
  continuityCount: number | null;
}

export const RECENTLY_SEEN_WINDOW_DAYS = 14;

const DAY_MS = 86_400_000;
const EDINBURGH_FALLBACK_PLACES =
  /\b(edinburgh|leith|portobello|currie|balerno|ratho|newbridge|kirkliston|south\s+queensferry|queensferry|cramond|juniper\s+green|south\s+gyle|hermiston|newcraighall)\b/i;
const OUTSIDE_EDINBURGH_PLACES =
  /\b(dalkeith|bonnyrigg|loanhead|lasswade|penicuik|gorebridge|rosewell|danderhall|easthouses|musselburgh|whitecraig|prestonpans|tranent|roslin|east\s+calder|livingston|broxburn|rosyth|fife|midlothian|east\s+lothian|west\s+lothian)\b/i;
const COUNCIL_PROMOTER = /\bcity\s+of\s+edinburgh\s+council\b/i;
const CYCLE_SPECIFIC_TAG =
  /^(cycle_related|cycle_related_medium_confidence|cycle_related_high_confidence|cycle_related_ai_reviewed)$/;
const HIGH_CONFIDENCE_ACTIVE_TRAVEL_TAG =
  /^(cycle_related_high_confidence|cycle_related_ai_reviewed)$/;
const EVENT_CLOSURE =
  /\b(parade|fun\s*run|marathon|race|procession|festival|fireworks|carnival|gala|charity\s*(run|walk|cycle|ride)|rally|demonstration|protest\s*march|rolling\s+closure)\b/i;
const UTILITY_PROMOTER =
  /\b(scottish\s+water|openreach|telecom|cityfibre|virgin\s+media|vodafone|electricity|electric|gas|energy\s+network|pipelines?|sp\s+energy|last\s+mile|network\s+company)\b/i;
const UTILITY_WORK =
  /\b(lead\s+renewal|water\s+(main|mains|service|pipe)|gas\s+(main|mains|service|pipe)|sewer|stormwater|telecoms?|openreach|bt\s+openreach|manhole|fire\s+hydrant|fh\s+cover|utility\s+cover|valve\s+cover|cctv|anpr|traffic\s*camera|camera\s*(column|pole|mast|base|foundation|install|unit)|electrical?\s+(main|mains|cable|supply|service|connect|duct|infrastructure)|electricity\s+(main|mains|cable|supply|service|connect|duct)|power\s+(main|mains|cable|supply|service|connect|duct)|install(?:ing)?\s+(?:a\s+|new\s+)?(?:hv\s+)?(?:cables?|ducts?|gas\s+main)|lay(?:ing)?\s+(?:a\s+|new\s+)?(?:hv\s+)?(?:cables?|ducts?|gas\s+main)|excavat(?:e|ing|ion)[^.]{0,80}\b(cable|duct|gas|water|electric|main|mains)\b|street\s*light(?:ing)?\s+(?:upgrade|replacement|column|install)|column\s+replacement|junction\s+box|network\s+access|new\s+housing\s+development|housing\s+development)\b/i;

export function toText(value: unknown): string {
  return value == null ? '' : String(value).trim();
}

export function normalizeTags(tags: unknown): string[] {
  if (!Array.isArray(tags)) return [];
  return tags.map(tag => toText(tag).toLowerCase()).filter(Boolean);
}

export function parseAnyDate(value: unknown): Date | null {
  const text = toText(value);
  if (!text) return null;

  const ukDate = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (ukDate) {
    const date = new Date(Number(ukDate[3]), Number(ukDate[2]) - 1, Number(ukDate[1]));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const isoDate = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDate) {
    const date = new Date(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function addDays(value: Date, days: number): Date {
  const next = startOfDay(value);
  next.setDate(next.getDate() + days);
  return next;
}

function calendarDaysBetween(from: Date, to: Date): number {
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / DAY_MS);
}

export function itemDates(item: RoadworkItem): { start: Date | null; end: Date | null } {
  const latestStart = parseAnyDate(item.latest_start);
  const latestEnd = parseAnyDate(item.latest_end);
  const plannedStart = parseAnyDate(item.planned_start);
  const plannedEnd = parseAnyDate(item.planned_end);
  return {
    start: latestStart ?? plannedStart ?? latestEnd ?? plannedEnd,
    end: latestEnd ?? plannedEnd ?? latestStart ?? plannedStart,
  };
}

export function formatDate(value: unknown, includeYear = true): string {
  const date = value instanceof Date ? value : parseAnyDate(value);
  if (!date) return 'Unknown';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(includeYear ? { year: 'numeric' as const } : {}),
  });
}

export function formatDateRange(item: RoadworkItem): string {
  const { start, end } = itemDates(item);
  if (!start && !end) return 'Dates not supplied';
  if (start && end && start.toDateString() !== end.toDateString()) {
    const sameYear = start.getFullYear() === end.getFullYear();
    return formatDate(start, !sameYear) + ' – ' + formatDate(end);
  }
  return formatDate(start ?? end);
}

export function durationDays(item: RoadworkItem): number {
  const { start, end } = itemDates(item);
  if (start && end) return Math.max(1, calendarDaysBetween(start, end) + 1);
  const direct = Number.parseInt(toText(item.duration), 10);
  if (Number.isFinite(direct)) return Math.max(1, direct);
  return -1;
}

export function formatDuration(item: RoadworkItem): string {
  const days = durationDays(item);
  if (days < 0) return 'Duration not supplied';
  return days + ' day' + (days === 1 ? '' : 's');
}

function pointInRing(
  point: readonly [number, number],
  ring: readonly (readonly number[])[]
): boolean {
  const [x, y] = point;
  let inside = false;

  for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index, index += 1) {
    const currentPoint = ring[index];
    const previousPoint = ring[previous];
    const currentX = Number(currentPoint?.[0]);
    const currentY = Number(currentPoint?.[1]);
    const previousX = Number(previousPoint?.[0]);
    const previousY = Number(previousPoint?.[1]);
    if (![currentX, currentY, previousX, previousY].every(Number.isFinite)) continue;

    const intersects =
      currentY > y !== previousY > y &&
      x < ((previousX - currentX) * (y - currentY)) / (previousY - currentY) + currentX;
    if (intersects) inside = !inside;
  }

  return inside;
}

export function isPointInEdinburghBoundary(point: readonly [number, number]): boolean {
  return EDINBURGH_COUNCIL_BOUNDARY.some(polygon => {
    const [outerRing, ...holes] = polygon;
    if (!outerRing || !pointInRing(point, outerRing)) return false;
    return !holes.some(hole => pointInRing(point, hole));
  });
}

export function isEdinburghWork(item: RoadworkItem): boolean {
  const coords = getRoadworkCoords(item);
  if (coords) return isPointInEdinburghBoundary(coords);

  const addressText = [item.location, item.full_address].map(toText).join(' ');
  const explicitEdinburghAddress =
    /,\s*(?:[a-z' -]+,\s*)?edinburgh(?:\s|$)/i.test(addressText) ||
    /\b(city\s+of\s+edinburgh)\b/i.test(addressText);
  if (explicitEdinburghAddress) return true;
  if (OUTSIDE_EDINBURGH_PLACES.test(addressText)) return false;
  if (EDINBURGH_FALLBACK_PLACES.test(addressText)) return true;

  return COUNCIL_PROMOTER.test(toText(item.promoter_name));
}

export function isEventClosure(description: string): boolean {
  return EVENT_CLOSURE.test(description);
}

export function isUtilityInfrastructure(item: RoadworkItem): boolean {
  const description = toText(item.description);
  const promoter = toText(item.promoter_name);
  const combined = [
    description,
    item.works_type,
    promoter,
  ].map(toText).join(' ');
  return UTILITY_PROMOTER.test(promoter) || UTILITY_WORK.test(combined);
}

export function isLineMarkingWork(description: string): boolean {
  if (/\b(sewer|pipe|water|gas|duct|cable|main|mains)\b/i.test(description)) return false;
  return /\b(line\s*marking|road\s*marking|road\s*markings|lane\s*marking|lane\s*markings|white\s*line|yellow\s*line|centre\s*line|center\s*line|thermoplastic\s*(line|marking|markings)|road\s*line|cycle\s*lane\s*line|carriageway\s*line)\b/i.test(description);
}

function isGenericLineMarkingOnly(description: string): boolean {
  if (!isLineMarkingWork(description)) return false;

  // Swept-path dots describe vehicle/tram tracking space, not work to a cycle
  // facility. Some source records mention a nearby cycle lane and are wrongly
  // AI-tagged as active travel, so the marking itself is the decisive signal.
  if (
    /\bswept\s+path\s+dots?\b/i.test(description) &&
    !/\bred\s+cycle\s+lane\b/i.test(description)
  ) return true;

  const substantiveActiveTravelWork =
    /\b(cycl(e|ing|eway|e\s*lane|e\s*path|e\s*route|e\s*track)|bike\s*(lane|path|route)|active\s+travel|ccwel|shared\s*(use|path|route)|traffic\s+calming|paving|footway\s+(works|construction|widen|improv|upgrad|reconstruct|refurb|reinstat|surfac\w*|slabb|pav|install)|kerb(?:ing|line)?[^.]{0,40}\bfootway|build[\s-]?outs?|drop(?:ped)?\s*kerb|tactile\s+pav|(?:new|install(?:ation|ing)?|upgrad(?:e|ing)|reconstruct(?:ion|ing)?|relocat(?:e|ion|ing))\s+(?:controlled\s+)?(?:pedestrian\s+)?crossing)\b/i.test(description);

  return !substantiveActiveTravelWork;
}

export function getWorkSubcategory(item: RoadworkItem): WorkSubcategory | null {
  const description = toText(item.description).toLowerCase();
  const fullText = [
    description,
    item.location,
    item.full_address,
    item.works_type,
  ].map(toText).join(' ').toLowerCase();
  const hasWalkingSurfaceContext =
    /\b(?:footway|footpath|f\s*\/?\s*way|fw)\b/.test(fullText) &&
    /\b(?:defects?|remedial|paving|slurry|seal(?:ing)?|reinstat\w*)\b/.test(fullText);

  if (isEventClosure(description)) return null;

  if (/cycle\s*hang(a|e)r|bike\s*hang(a|e)r|cycle\s*locker|cycle\s*rack|bike\s*rack|sheffield\s*stand|cycle\s*storage|secure\s*cycle|on[\s-]?street\s*cycle\s*park|cycle\s*parking/.test(description)) {
    return 'cycle_hanger';
  }

  if (/shared\s*(use|path|route|surface)|multi[\s-]?use\s*(path|route|trail)|greenway|off[\s-]?road\s*(cycle\s*)?(path|route)|(?:widen(?:ing)?|upgrad(?:e|ing)|improv(?:e|ement|ing))\s+(?:of\s+)?(?:an?\s+)?existing\s+(?:walking\s+|cycle\s+)?path|(?:walking\s+|cycle\s+)?path\s+(?:widen(?:ing)?|upgrad(?:e|ing)|improv(?:e|ement|ing))/.test(fullText)) {
    return 'shared_path';
  }

  if (/\b(toucan|puffin|pegasus|zebra\s+crossing)\b|signal(?:led)?\s*crossing|crossing\s*signal|controlled\s+pedestrian\s+crossing|pedestrian\s*(crossing\s*)?signal|cycle\s*signal|signal\s+installation|traffic\s+signals?\s+and\s+junction\s+upgrade|(?:new|staggered|upgrad(?:e|ing)|install(?:ation|ing)?)\s+(?:controlled\s+)?(?:pedestrian\s+)?crossing|pedestrian\s+(?:drop\s+)?crossing(?:\s+points?)?|pedestrian\s+refuge|refuge\s+island|keep\s+left\s+bollard/.test(description)) {
    return 'signals_crossings';
  }

  if (/\bcycl(e|ing|ist|eway|eway|e\s*lane|e\s*path|e\s*route|e\s*track)|bike\s*(lane|path|route)|bicycle\s*(lane|path)|segregated\s*(cycle\s*)?(path|route)|floating\s+bus\s+stop|protected\s+cycleway|\bccwel\b|city\s+centre\s+west[\s-]?east\s+link/.test(fullText)) {
    return 'cycling';
  }

  if (/\btactile|drop(?:ped)?\s*kerb|kerb(?:ing)?\s*(?:and\s+)?(?:slabb(?:ing)?|ramp|drop|dish)|dished?\s*kerb|raised\s+table|continuous\s+footway|new\s+(footway|footpath)|footway\s+(?:works|construction|widen|improv|upgrad|reconstruct|refurb|reinstat|resurfac|slab|pav|defect|new|install|build[\s-]?outs?)|footpath\s+(construction|widen|improv|upgrad|reconstruct|refurb|resurfac|defect)|reinstatement\s+of[^.]{0,100}\bfootway|(?:install|add)(?:ing|ation)?[^.]{0,40}(?:footway\s+)?build[\s-]?outs?|\bbuild[\s-]?outs?\b|build[\s-]?outs?[^.]{0,40}footway|\bslabbing\b|(?:installation\s+of\s+)?(?:socketed\s+)?bollards?\b|installation\s+of\s+planters?\b|pedestrian\s+(?:safety|improv|infrastructure|priority|access\s+improv)|public\s+realm\s+enhancement/.test(description) || hasWalkingSurfaceContext) {
    return 'pedestrian';
  }

  if (/\b(resurface|resurfacing|surface\s*dress|micro[\s-]?asphalt|cold\s*plan|tarmac|macadam|skid\s*resist|road\s*inlay|road\s*overlay|carriageway\s*patch|road\s*marking|line\s*marking|thermoplastic|junction\s+(upgrade|improvement|repair|reconstruction|reconfiguration)|reconstruction\s+of(?:\s+a)?\s+(?:signal(?:led)?\s+)?junction|traffic\s+calming|road\s+safety)\b/.test(description) || isLineMarkingWork(description)) {
    return 'road_improvement';
  }

  return null;
}

export function isActiveTravelWork(item: RoadworkItem): boolean {
  const description = toText(item.description);
  if (
    /\bevent\b/i.test(toText(item.works_type)) ||
    isEventClosure([description, item.location, item.works_type].map(toText).join(' ')) ||
    isUtilityInfrastructure(item) ||
    isGenericLineMarkingOnly(description)
  ) return false;

  const tags = normalizeTags(item.tags);
  const hasCycleSpecificTag = tags.some(tag => CYCLE_SPECIFIC_TAG.test(tag));
  const hasHighConfidenceTag = tags.some(tag => HIGH_CONFIDENCE_ACTIVE_TRAVEL_TAG.test(tag));
  const category = getWorkSubcategory(item);
  if (!category) {
    return hasHighConfidenceTag && COUNCIL_PROMOTER.test(toText(item.promoter_name));
  }

  if (category !== 'road_improvement') return true;

  const explicitCycleSurface =
    /\b(cycl(e|ing|eway|e\s*lane|e\s*path)|bike\s*(lane|path)|active\s+travel)\b/i.test(description);
  const isCouncilTrafficCalming =
    /\btraffic\s+calming\b/i.test(description) &&
    COUNCIL_PROMOTER.test(toText(item.promoter_name));

  return explicitCycleSurface || isCouncilTrafficCalming || (
    hasCycleSpecificTag &&
    COUNCIL_PROMOTER.test(toText(item.promoter_name))
  );
}

export function getWorkTiming(item: RoadworkItem, now = new Date()): WorkTiming {
  const { start, end } = itemDates(item);
  if (!start && !end) return 'unscheduled';

  const today = startOfDay(now);
  const effectiveStart = start ? startOfDay(start) : end ? startOfDay(end) : null;
  const effectiveEnd = end ? startOfDay(end) : effectiveStart;
  if (!effectiveStart || !effectiveEnd) return 'unscheduled';

  if (effectiveEnd < today) return 'ended';
  if (effectiveStart <= today && effectiveEnd >= today) return 'live';
  if (effectiveStart <= addDays(today, 30)) return 'next_30_days';
  if (effectiveStart <= addDays(today, 90)) return 'next_90_days';
  return 'later';
}

export function matchesTimeFilter(
  item: RoadworkItem,
  filter: WorkTimeFilter,
  now = new Date()
): boolean {
  if (filter === 'all') return true;
  const timing = getWorkTiming(item, now);
  if (filter === 'now_and_soon') {
    if (timing === 'live') return true;
    const start = itemDates(item).start;
    return Boolean(
      start &&
      startOfDay(start) > startOfDay(now) &&
      startOfDay(start) <= addDays(now, 14)
    );
  }
  return timing === filter;
}

export function getSchedulePresentation(
  item: RoadworkItem,
  now = new Date()
): { label: string; detail: string; timing: WorkTiming } {
  const timing = getWorkTiming(item, now);
  const { start, end } = itemDates(item);

  if (timing === 'live') {
    const daysRemaining = end ? calendarDaysBetween(now, end) : null;
    return {
      timing,
      label: 'Happening now',
      detail: end
        ? (daysRemaining === 0 ? 'Due to finish today' : 'Until ' + formatDate(end))
        : 'End date not supplied',
    };
  }

  if (timing === 'next_30_days' || timing === 'next_90_days' || timing === 'later') {
    const daysUntil = start ? calendarDaysBetween(now, start) : null;
    return {
      timing,
      label:
        daysUntil === 0
          ? 'Starts today'
          : daysUntil === 1
            ? 'Starts tomorrow'
            : daysUntil != null
              ? 'Starts in ' + daysUntil + ' days'
              : 'Upcoming',
      detail: start ? formatDate(start) : 'Start date not supplied',
    };
  }

  if (timing === 'ended') {
    return {
      timing,
      label: 'Past work',
      detail: end ? 'Ended ' + formatDate(end) : 'End date not supplied',
    };
  }

  return {
    timing,
    label: 'Schedule unconfirmed',
    detail: 'Dates not supplied by the source',
  };
}

export function getDiscoveryState(
  item: RoadworkItem,
  now = new Date(),
  windowDays = RECENTLY_SEEN_WINDOW_DAYS
): DiscoveryState {
  if (item.is_new === true) return 'new_refresh';
  const firstSeen = parseAnyDate(item.first_seen_at);
  if (!firstSeen) return 'established';
  const ageDays = (now.getTime() - firstSeen.getTime()) / DAY_MS;
  return ageDays >= -1 && ageDays <= windowDays ? 'recently_seen' : 'established';
}

export function formatFirstSeen(item: RoadworkItem, now = new Date()): string | null {
  const firstSeen = parseAnyDate(item.first_seen_at);
  if (!firstSeen) return null;
  const days = calendarDaysBetween(firstSeen, now);
  if (days <= 0) return 'First seen today';
  if (days === 1) return 'First seen yesterday';
  if (days < 14) return 'First seen ' + days + ' days ago';
  return 'First seen ' + formatDate(firstSeen);
}

export function hasHighImpact(item: RoadworkItem): boolean {
  return getImpactRank(toText(item.traffic_impact)) >= 3;
}

export function getRoadworkId(item: RoadworkItem): string {
  return toText(item.id) || toText(item.reference);
}

export function searchableText(item: RoadworkItem): string {
  const category = getWorkSubcategory(item);
  return [
    item.reference,
    item.description,
    item.location,
    item.full_address,
    item.promoter_name,
    item.status,
    item.traffic_impact,
    item.works_type,
    normalizeTags(item.tags).join(' '),
    category?.replace(/_/g, ' '),
  ].map(toText).join(' ').toLowerCase();
}

const TIMING_RANK: Record<WorkTiming, number> = {
  live: 0,
  next_30_days: 1,
  next_90_days: 2,
  later: 3,
  ended: 4,
  unscheduled: 5,
};

export function compareWorks(a: RoadworkItem, b: RoadworkItem, now = new Date()): number {
  const aTiming = getWorkTiming(a, now);
  const bTiming = getWorkTiming(b, now);
  if (aTiming !== bTiming) return TIMING_RANK[aTiming] - TIMING_RANK[bTiming];

  if (aTiming === 'live') {
    const impactDifference =
      getImpactRank(toText(b.traffic_impact)) - getImpactRank(toText(a.traffic_impact));
    if (impactDifference !== 0) return impactDifference;
    const aEnd = itemDates(a).end?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const bEnd = itemDates(b).end?.getTime() ?? Number.MAX_SAFE_INTEGER;
    return aEnd - bEnd;
  }

  if (aTiming === 'ended') {
    const aEnd = itemDates(a).end?.getTime() ?? 0;
    const bEnd = itemDates(b).end?.getTime() ?? 0;
    return bEnd - aEnd;
  }

  const aStart = itemDates(a).start?.getTime() ?? Number.MAX_SAFE_INTEGER;
  const bStart = itemDates(b).start?.getTime() ?? Number.MAX_SAFE_INTEGER;
  if (aStart !== bStart) return aStart - bStart;
  return getImpactRank(toText(b.traffic_impact)) - getImpactRank(toText(a.traffic_impact));
}

function finiteNumber(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number.parseFloat(toText(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function valueFrom(record: Record<string, unknown> | null | undefined, keys: string[]): unknown {
  if (!record) return undefined;
  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null) return record[key];
  }
  return undefined;
}

export function getRoadworksDataHealth(
  status: RoadworksStatusResponse | null,
  now = new Date()
): DataHealth {
  if (!status) {
    return {
      level: 'unknown',
      label: 'Status unavailable',
      summary: 'Works loaded, but refresh health could not be verified.',
      details: ['The map can still be used. Check the source record before relying on a date.'],
      updatedAt: null,
      ageHours: null,
      coverageStart: null,
      coverageEnd: null,
      sourceCount: null,
      previousCount: null,
      countChangePercent: null,
      cacheHits: null,
      queryCalls: null,
      networkRequests: null,
      lookbackDays: null,
      continuityCount: null,
    };
  }

  const meta = status.meta_summary ?? {};
  const input = status.meta_input ?? {};
  const lastRun = status.last_run ?? {};
  const freshness = status.freshness ?? {};
  const continuity = status.continuity ?? {};
  const updatedAt = parseAnyDate(
    valueFrom(freshness, ['published_at']) ??
    valueFrom(lastRun, ['finished_at', 'saved_at', 'started_at'])
  );
  const ageHours = updatedAt ? Math.max(0, (now.getTime() - updatedAt.getTime()) / 3_600_000) : null;
  const coverageStart = parseAnyDate(
    valueFrom(freshness, ['query_from']) ??
    valueFrom(input, ['from', 'from_date']) ??
    valueFrom(lastRun, ['from_date'])
  );
  const coverageEnd = parseAnyDate(
    valueFrom(freshness, ['query_to']) ??
    valueFrom(input, ['to', 'to_date']) ??
    valueFrom(lastRun, ['to_date'])
  );
  const sourceCount =
    finiteNumber(status.works_count) ??
    finiteNumber(valueFrom(meta, ['works_after_dedupe', 'current_snapshot_count']));
  const previousCount = finiteNumber(
    valueFrom(continuity, ['previous_count']) ??
    valueFrom(meta, ['previous_snapshot_count', 'prior_snapshot_count'])
  );
  const countChangePercent =
    sourceCount != null && previousCount != null && previousCount > 0
      ? ((sourceCount - previousCount) / previousCount) * 100
      : null;
  const queryCalls = finiteNumber(valueFrom(meta, ['query_calls', 'slice_count']));
  const cacheHits = finiteNumber(
    valueFrom(freshness, ['cache_hits']) ?? valueFrom(meta, ['cache_hits'])
  );
  const networkRequests = finiteNumber(
    valueFrom(freshness, ['network_requests']) ??
    valueFrom(meta, ['network_requests', 'cache_misses'])
  );
  const failures = finiteNumber(valueFrom(meta, ['failure_count', 'failures'])) ?? 0;
  const truncations = finiteNumber(valueFrom(meta, ['truncation_event_count', 'truncation_count'])) ?? 0;
  const warnings = finiteNumber(valueFrom(meta, ['warning_count'])) ?? 0;
  const returnCode = finiteNumber(valueFrom(lastRun, ['return_code']));
  const lookbackDays = finiteNumber(
    valueFrom(freshness, ['lookback_days']) ??
    valueFrom(lastRun, ['lookback_days']) ??
    valueFrom(meta, ['lookback_days'])
  );
  const continuityCount = finiteNumber(
    valueFrom(continuity, [
      'retained_previous_ongoing_count',
      'continuity_count',
      'continuity_preserved_count',
      'ongoing_carried_forward_count',
      'carried_forward_ongoing_count',
    ]) ??
    valueFrom(meta, [
      'continuity_count',
      'continuity_preserved_count',
      'ongoing_carried_forward_count',
      'carried_forward_ongoing_count',
    ])
  );
  const freshSource = valueFrom(freshness, ['fresh_source']) === true;
  const continuityPassed = valueFrom(continuity, ['passed']);
  const lastAttemptStatus = toText(
    valueFrom(freshness, ['last_attempt_status']) ?? valueFrom(lastRun, ['status'])
  ).toLowerCase();
  const explicitlyStale = valueFrom(freshness, ['is_stale']) === true;
  const cacheRefreshMode = toText(
    valueFrom(meta, ['cache_refresh_mode', 'cache_mode']) ??
    valueFrom(lastRun, ['cache_refresh_mode'])
  ).toLowerCase();
  const cacheRefreshCount = finiteNumber(
    valueFrom(meta, ['cache_refresh_count', 'cache_refreshed_count'])
  ) ?? 0;
  const cacheExplicitlyRefreshed =
    freshSource ||
    cacheRefreshCount > 0 ||
    /\b(refresh|bypass|uncached|fresh)\b/.test(cacheRefreshMode);

  const problems: string[] = [];
  if (
    status.ok === false ||
    (returnCode != null && returnCode !== 0) ||
    (lastAttemptStatus && !['published', 'success', 'succeeded', 'ok'].includes(lastAttemptStatus))
  ) {
    problems.push('The latest source refresh did not complete successfully.');
  }
  if (ageHours == null) {
    problems.push('The source did not provide a refresh time.');
  } else if (explicitlyStale || ageHours > 36) {
    const ageDays = Math.max(2, Math.floor(ageHours / 24));
    problems.push('The latest confirmed refresh is ' + ageDays + ' days old.');
  }
  if (failures > 0) {
    problems.push(failures + ' source request' + (failures === 1 ? '' : 's') + ' failed.');
  }
  if (warnings > 0) {
    problems.push(warnings + ' source warning' + (warnings === 1 ? ' was' : 's were') + ' reported.');
  }
  if (continuityPassed === false) {
    problems.push('The latest snapshot did not pass its continuity checks.');
  }
  if (countChangePercent != null && countChangePercent <= -10) {
    problems.push(
      'The latest snapshot has ' +
      Math.abs(Math.round(countChangePercent)) +
      '% fewer records than the previous refresh.'
    );
  }
  const cacheDominates =
    cacheHits != null &&
    queryCalls != null &&
    queryCalls >= 4 &&
    cacheHits / queryCalls >= 0.75 &&
    (networkRequests == null || cacheHits >= networkRequests * 2);
  if (cacheDominates && !cacheExplicitlyRefreshed) {
    problems.push(
      cacheHits +
      ' of ' +
      queryCalls +
      ' date windows came from cache; some later changes may be delayed.'
    );
  }

  const details: string[] = [];
  if (updatedAt) details.push('Last refresh: ' + updatedAt.toLocaleString('en-GB'));
  if (coverageStart && coverageEnd) {
    details.push('Scheduled coverage: ' + formatDate(coverageStart) + ' to ' + formatDate(coverageEnd));
  }
  if (sourceCount != null) details.push(sourceCount.toLocaleString('en-GB') + ' source records loaded');
  if (freshSource && networkRequests != null) {
    details.push(networkRequests.toLocaleString('en-GB') + ' live source requests checked');
  }
  if (truncations > 0 && failures === 0 && warnings === 0) {
    details.push(
      truncations.toLocaleString('en-GB') +
      ' busy source window' +
      (truncations === 1 ? ' was' : 's were') +
      ' split and rechecked'
    );
  }
  if (lookbackDays != null) {
    details.push(lookbackDays + '-day lookback used to retain ongoing works');
  }
  if (continuityCount != null) {
    details.push(continuityCount.toLocaleString('en-GB') + ' ongoing records preserved for continuity');
  }

  if (problems.length > 0) {
    return {
      level: 'warning',
      label: 'Coverage warning',
      summary: problems[0],
      details: [...problems, ...details],
      updatedAt,
      ageHours,
      coverageStart,
      coverageEnd,
      sourceCount,
      previousCount,
      countChangePercent,
      cacheHits,
      queryCalls,
      networkRequests,
      lookbackDays,
      continuityCount,
    };
  }

  return {
    level: 'healthy',
    label: 'Data current',
    summary: updatedAt
      ? 'Refresh completed ' + formatRelativeRefresh(updatedAt, now) + '.'
      : 'The latest source refresh completed successfully.',
    details,
    updatedAt,
    ageHours,
    coverageStart,
    coverageEnd,
    sourceCount,
    previousCount,
    countChangePercent,
    cacheHits,
    queryCalls,
    networkRequests,
    lookbackDays,
    continuityCount,
  };
}

export function formatRelativeRefresh(updatedAt: Date | null, now = new Date()): string {
  if (!updatedAt) return 'at an unknown time';
  const minutes = Math.max(0, Math.round((now.getTime() - updatedAt.getTime()) / 60_000));
  if (minutes < 2) return 'just now';
  if (minutes < 60) return minutes + ' minutes ago';
  const hours = Math.round(minutes / 60);
  if (hours < 24) return hours + ' hour' + (hours === 1 ? '' : 's') + ' ago';
  const days = Math.round(hours / 24);
  return days + ' day' + (days === 1 ? '' : 's') + ' ago';
}
